import {
  NetworkService,
  Parameters,
  requireDefined,
  SharedPermissionService,
  SharedUserService,
} from '@agroone-front/shared'
import { DateFormat, formatDateV2 } from '@agroone/dates'
import {
  Crop,
  CropActivity,
  FeatureCollection,
  Field,
  Location,
  MsSqlPaginatedData,
  Offer,
  PesticideProductLite,
  Scouting,
  UserPermissions,
  VirtualCrop,
} from '@agroone/entities'
import { HttpClient, HttpParams } from '@angular/common/http'
import { inject, Injectable } from '@angular/core'
import { environment } from '@front-app-environments/environment'
import { Actions, ofType } from '@ngrx/effects'
import { Store } from '@ngrx/store'
import {
  debounceTime,
  firstValueFrom,
  from,
  last,
  map,
  merge,
  mergeMap,
  Observable,
  of,
  scan,
  switchMap,
  take,
  tap,
} from 'rxjs'
import {
  addCrop,
  CropHttpParams,
  loadCrops,
  loadCropsSuccess,
} from '../../../scene/crop-management/crop/store/actions/crop.actions'
import { CropState } from '../../../scene/crop-management/crop/store/reducers/crop.reducer'
import { selectCrops } from '../../../scene/crop-management/crop/store/selectors/crop.selectors'

type RequestConfig = { params?: Record<string, string | number | boolean> }

export interface VirtualCropPlantingPayload {
  plantingId?: string | null
  seedingRate?: number | null
  seedingRateUnit?: string | null
  lotNumber?: string | null
  germinationDate?: string | null
  weatherConditions?: string | null
  soilConditions?: string | null
  seedLines?: number | null
  beds?: number | null
  bedWidth?: number | null
  bedWidthUnit?: string | null
  initialPlannedHarvestDate?: string | null
  specificationId?: number | null
}

/** Body sent by the virtual crop form: the form values, not a complete VirtualCrop */
export interface VirtualCropPayload {
  id?: number
  supplierReference: string | null
  cropTypeId: number | null
  areaUnit: string
  intendedArea: number | null
  region: string
  campaignId: number | null
  growingSystem: string | null
  notes: string | null
  technician: number | null
  cooperativeId: number | null
  farmId: number | undefined
  isVirtual: boolean
  location: Location | null
  boundary: FeatureCollection | null
  town: string | null
  zipCode: string | null
  countryId: number | null
  planting?: VirtualCropPlantingPayload
  varietyId?: number | null
  plantedDate?: string | null
  plantedArea?: number | null
  varietalTypeId?: number | null
}

@Injectable({
  providedIn: 'root',
})
export class CropService {
  private readonly http = inject(HttpClient)
  private readonly cropStore = inject(Store<CropState>)
  private readonly actions$ = inject(Actions)
  private readonly userService = inject(SharedUserService)
  private readonly networkService = inject(NetworkService)
  private readonly permissionService = inject(SharedPermissionService)
  private cropsUrlApi: string = `${environment.apiUrl}${environment.crops}`
  private virtualCropsUrlApi: string = `${environment.apiUrl}${environment.crops}/virtual`

  /**
   * Get crop by id
   * first from the  crop state, else from the remote server
   *
   * @param id
   * @returns
   */
  public get(id: number): Observable<Crop> {
    return this.cropStore.select(selectCrops).pipe(
      take(1),
      debounceTime(200),
      mergeMap((state) => {
        const savedCrop = state.find((si) => si.id === id)
        if (savedCrop) {
          return of(savedCrop)
        }

        // In case we never had this crop in the local state we fetch the specific crop
        return this.http.get<Crop>(`${this.cropsUrlApi}/${id}`).pipe(
          tap((crop) => {
            this.cropStore.dispatch(addCrop({ payload: { crop, options: { insert: false } } }))
          }),
        )
      }),
    )
  }

  /**
   * Get all crops
   *
   * @param options
   * @returns
   */
  /**
   * Stream all crops page by page: emits the first page as soon as it arrives, then re-emits the
   * cumulative list each time a subsequent page resolves. Lets the UI render progressively instead of
   * waiting for every page. Page order is irrelevant here (the list is sorted client-side).
   */
  public streamAll(options: CropHttpParams): Observable<Crop[]> {
    const config: RequestConfig = {}
    if (options.params) {
      config.params = this.networkService.getHttpParams(options.params)
    }

    const queryParams = new URLSearchParams()
    if (options.fieldId) {
      queryParams.set('fieldId', String(options.fieldId))
    }
    if (options.includeVirtual) {
      queryParams.set('includeVirtual', 'true')
    }
    const query = queryParams.toString() ? `?${queryParams.toString()}` : ''

    if (
      this.permissionService.isGranted([
        UserPermissions.CROPS_VIEW,
        UserPermissions.CROPS_EDIT,
        // UserPermissions.THIRD_PARTY_APPLICATOR_TASK_EDIT,
        UserPermissions.WORKLIST_EDIT,
        UserPermissions.CROP_MODULE_ACCESS,
      ])
    ) {
      return this.http.get<MsSqlPaginatedData<Crop>>(`${this.cropsUrlApi}${query}`, config).pipe(
        switchMap((response: MsSqlPaginatedData<Crop>) => {
          if (response.currentPage >= response.pageCount) {
            return of(response.data)
          }
          const remainingPages = Array.from({ length: response.pageCount - 1 }, (_, i) =>
            this.http
              .get<MsSqlPaginatedData<Crop>>(`${this.cropsUrlApi}${query}`, this.getConfig(config, i + 1))
              .pipe(map((page) => page.data)),
          )
          return merge(of(response.data), ...remainingPages).pipe(
            scan((acc: Crop[], pageData: Crop[]) => acc.concat(pageData), [] as Crop[]),
          )
        }),
      )
    } else {
      return of([])
    }
  }

  /**
   * Complete crop list as a single emission. Built on {@link streamAll} but waits for every page,
   * for callers that need the full set at once (e.g. field-scoped lookups).
   */
  public getAll(options: CropHttpParams): Observable<Crop[]> {
    return this.streamAll(options).pipe(last())
  }

  /**
   * Get virtual crop by id
   *
   * @param id
   * @returns
   */
  public getVirtual(id: number): Observable<Crop> {
    return this.http.get<Crop>(`${this.virtualCropsUrlApi}/${id}`)
  }

  /**
   * Get all crops
   *
   * @param options
   * @returns
   */
  public getAllVirtual(options: CropHttpParams): Observable<Crop[]> {
    const config: RequestConfig = {}
    if (options.params) {
      config.params = this.networkService.getHttpParams(options.params)
    }

    return of(
      this.permissionService.isGranted([
        UserPermissions.CROPS_VIEW,
        UserPermissions.CROPS_EDIT,
        UserPermissions.CROP_MODULE_ACCESS,
      ]),
    ).pipe(
      switchMap((hasPermission) => {
        if (hasPermission) {
          return this.networkService.getAllFromPaginated<Crop>(`${this.virtualCropsUrlApi}`, config)
        }

        return of([])
      }),
    )
  }

  /**
   * Return the crops by field ID
   * Check the store then the remote
   *
   * @param field
   * @returns
   */
  public getByField(field: Field): Observable<Crop[]> {
    return this.cropStore.select(selectCrops).pipe(
      take(1),
      mergeMap((state) => {
        state = state.filter((si) => si.fieldId === field.id)
        if (state.length) {
          this.fetchByField(field).subscribe()
          return of(state)
        } else {
          return this.fetchByField(field)
        }
      }),
    )
  }

  /**
   * Add crop
   *
   * @param crop
   * @param options
   * @returns
   */
  public add(crop: Crop | VirtualCrop | VirtualCropPayload): Observable<Crop> {
    return this.http.post<Crop>(`${this.cropsUrlApi}`, crop)
  }

  /**
   * Update crop
   *
   * @param crop
   * @returns
   */
  public update(crop: Crop | VirtualCrop | VirtualCropPayload): Observable<Crop> {
    return this.http.put<Crop>(`${this.cropsUrlApi}`, crop)
  }

  public unclose(cropId: number): Observable<Crop> {
    return this.http.patch<Crop>(`${this.cropsUrlApi}/${cropId}/unclose`, {})
  }

  /**
   * @param item
   * @param patchValues
   * @returns
   */
  public patch(
    item: Pick<Crop | VirtualCrop, 'id' | 'region' | 'isVirtual'>,
    patchValues: { [key: string]: unknown },
  ): Observable<Crop> {
    const valuesToPatch = {
      ...patchValues,
      id: item.id,
      region: item.isVirtual ? this.userService.requireCurrentUser().regionName : item.region,
      isVirtual: item.isVirtual,
    }
    return this.http.patch<Crop>(this.cropsUrlApi, valuesToPatch)
  }

  public delete(id: number): Observable<Crop> {
    const params: HttpParams = new HttpParams().set(
      'date',
      requireDefined(formatDateV2(new Date(), DateFormat.DATETIME_OFFSET), 'the formatted date'),
    )
    return this.http.delete<Crop>(`${this.cropsUrlApi}/${id}`, { params })
  }

  public getDuplicateTargets(cropId: number): Observable<Crop[]> {
    return this.http.get<Crop[]>(`${this.cropsUrlApi}/${cropId}/duplicate-targets`)
  }

  public getActivitiesByCrop(cropId: number): Observable<CropActivity[]> {
    const params: Parameters = {
      filters: {},
    }

    const config: RequestConfig = {}
    config.params = this.networkService.getHttpParams(params)

    return from(
      this.networkService.getAllFromPaginated<CropActivity>(
        `${environment.apiUrl}${environment.activities}/${cropId}`,
        config,
      ),
    ).pipe(map((activities) => activities?.map((activity) => new CropActivity(activity)) ?? []))
  }

  public async getByGeohash(geohashes: string[]): Promise<Crop[]> {
    const result: Crop[] = (
      await Promise.all(
        geohashes.map((hash) => {
          const filter =
            `?filter=region="${this.userService.requireCurrentUser().regionName}",` +
            'endDate NOTEXISTS,' +
            `geohash STARTSWITH "${hash}"`
          return firstValueFrom(this.http.get<Crop[]>(`${this.cropsUrlApi}${filter}`))
        }),
      )
    ).reduce((array: Crop[], cs: Crop[]) => {
      array = array.concat(cs)
      return array
    }, [])
    return result
  }

  public getCropBoundaries(crops: Crop[]): Observable<Crop[] | undefined> {
    if (crops.length) {
      return this.http
        .get<Crop[]>(
          `${environment.apiUrl}${environment.geolocation}/crop/boundaries?ids=${crops.map((i) => i.id).join(',')}`,
        )
        .pipe(
          map((res) => {
            if (res) {
              res = res.filter(Boolean).map((crop) => {
                const c = new Crop(crop as Crop)
                c.cropType = crops.find((x) => x.id === crop.id)?.cropType ?? c.cropType
                return c
              })
              return res
            }
          }),
        )
    }
    return of([])
  }

  /**
   * @param cropId
   * @param targetDate Cutoff (RG1) for pesticides past their Last Use Date: only products still
   * authorized as of this date are returned, unless exempted. Defaults to today server-side when
   * omitted.
   * @returns
   */
  public getAvailablePesticides(
    cropIds: number[],
    targetDate?: string | null,
  ): Observable<{
    pesticides: PesticideProductLite[]
    crops: Crop[]
  }> {
    const params: { cropIds: string; targetDate?: string } = { cropIds: cropIds.join(',') }
    if (targetDate) {
      params.targetDate = targetDate
    }
    return this.http.get<{ pesticides: PesticideProductLite[]; crops: Crop[] }>(
      `${this.cropsUrlApi}/available-pesticides`,
      { params },
    )
  }

  public downloadPdf(cropId: number, activity: string, filename?: string): Observable<string> {
    const params = filename ? new HttpParams().set('filename', filename) : undefined
    return this.http.get<string>(`${this.cropsUrlApi}/pdf/${cropId}/${activity}`, {
      params,
      responseType: 'text' as 'json',
    })
  }

  public downloadPhytoRecapPdf(cropId: number, filename?: string): Observable<string> {
    const params = filename ? new HttpParams().set('filename', filename) : undefined
    return this.http.get<string>(`${this.cropsUrlApi}/phyto-recap/${cropId}`, {
      params,
      responseType: 'text' as 'json',
    })
  }

  /**
   * Performs the network call and returns the crops for
   * a given field ID
   */
  private fetchByField(field: Field, includeHistorical: boolean = false): Observable<Crop[]> {
    if (!field) return of([])

    const params: Parameters = {
      filters: {
        region: {
          fieldName: 'region',
          operator: 'equality',
          value: field.region,
        },
      },
    }

    // Only filter for active crops if not requesting historical data
    if (!includeHistorical) {
      params.filters['endDate'] = {
        fieldName: 'endDate',
        operator: 'notexists',
      }
    } else {
      params.otherParams = { displayHistoricalCrops: true }
    }

    return from(this.getAll({ params, fieldId: field.id ?? undefined })).pipe(
      map((state) =>
        state.filter((si) => {
          if (si.fieldId !== field.id) {
            return false
          }
          if (si.region !== field.region) {
            return false
          }
          return true
        }),
      ),
    )
  }

  /**
   * Get historical crop data for a field (both active and closed crops)
   * Groups crops by type and year for auto-populating dropdown selections
   * @param field Field to get historical data for
   * @returns Observable of crops grouped by cropType and campaign
   */
  public getHistoricalDataByField(field: Field): Observable<Crop[]> {
    return this.fetchByField(field, true)
  }

  public getByFarm(farmId: number): Observable<Crop[]> {
    const params: Parameters = {
      filters: {
        farmId: {
          fieldName: 'farmId',
          operator: 'equality',
          value: farmId,
        },
        endDate: {
          fieldName: 'endDate',
          operator: 'notexists',
        },
      },
    }
    this.cropStore.dispatch(loadCrops({ params }))
    return this.actions$.pipe(
      ofType(loadCropsSuccess),
      map((v) => v.crops),
    )
  }

  private getConfig(config: RequestConfig, currentPage: number): RequestConfig {
    if (config.params) {
      config.params.page = currentPage + 1
    } else {
      config = {
        params: { page: currentPage + 1 },
      }
    }
    return config
  }

  public dispatchLoadCrops(): void {
    const filters = {
      region: {
        fieldName: 'region',
        operator: 'equality',
        value: this.userService.requireCurrentUser().regionName,
      },
    }
    const params = {
      pageLength: 500,
      filters,
    } as Parameters
    // The main crop list/map (crop.component.ts) reads exclusively from this store slice, so
    // virtual crops need to be included here to show up there at all.
    this.cropStore.dispatch(loadCrops({ params, includeVirtual: true }))
  }

  getOffersForACrop(cropId: number): Observable<Offer[]> {
    return this.http.get<Offer[]>(`${this.cropsUrlApi}/${cropId}/offers`)
  }

  getScoutingForACrop(cropId: number): Observable<Scouting> {
    return this.http.get<Scouting>(`${this.cropsUrlApi}/${cropId}/scouting`)
  }

  getLotNumbers(cropTypeId: number): Observable<string[]> {
    return this.http.get<string[]>(`${this.cropsUrlApi}/lot-numbers?cropTypeId=${cropTypeId}`)
  }
}
